import {
    collection,
    addDoc,
    deleteDoc,
    doc,
    getDocs,
    query,
    where,
    orderBy,
    serverTimestamp,
    Timestamp,
} from 'firebase/firestore'
import { auth, firestore } from '../config/firebase'
import { FileDoc, IFile } from '../../../shared/types/file.types'
import { COLLECTIONS } from '../../../shared/constants/collections'

const API_URL = import.meta.env.VITE_API_URL as string

export interface FileFilters {
    /** Filtruje soubory přiřazené konkrétní garáži (null = soubory bez garáže). */
    garageUid?: string | null
    /** Filtruje soubory přiřazené konkrétnímu servisnímu středisku (dealerovi). */
    dealerId?: string
    /** Filtruje soubory přiřazené konkrétnímu vozidlu. */
    vehicleUid?: string
    /** Filtruje soubory přiřazené konkrétnímu servisu. */
    vehicleServiceUid?: string
    /** Filtruje soubory přiřazené konkrétnímu převodu. */
    vehicleTransferUid?: string
}

export interface FileCreateData {
    /** Zobrazovaný název souboru (default: původní název souboru). */
    name?: string
    garageUid?: string
    /** Servisní středisko (dealer), ke kterému doklad patří — zmrazeno v okamžiku vzniku. */
    dealerId?: string
    vehicleUid?: string
    vehicleServiceUid?: string
    vehicleTransferUid?: string
}

interface IUseFileReturn {
    create: (file: File, data?: FileCreateData) => Promise<FileDoc>
    list: (filters?: FileFilters) => Promise<FileDoc[]>
    remove: (file: FileDoc) => Promise<void>
}

async function getAuthToken(): Promise<string> {
    const token = await auth.currentUser?.getIdToken()
    if (!token) throw new Error('User not authenticated')
    return token
}

const convertDoc = (id: string, data: Record<string, unknown>): FileDoc => ({
    id,
    ...data,
    createdAt: data.createdAt instanceof Timestamp
        ? data.createdAt.toDate()
        : new Date(data.createdAt as string),
} as FileDoc)

const useFile = (): IUseFileReturn => {

    /**
     * Nahraje soubor do Cloud Storage (přes API) a uloží záznam do Firestore.
     * Pole `url` v záznamu je permanentní Firebase Storage download URL.
     * @param file - Nativní File objekt (z input[type=file])
     * @param data - Metadata: název, garageUid, vehicleUid, vehicleServiceUid
     */
    const create = async (file: File, data?: FileCreateData): Promise<FileDoc> => {
        const token = await getAuthToken()

        // 1. Nahrání souboru do Cloud Storage
        const form = new FormData()
        form.append('file', file)
        if (data?.garageUid) form.append('garageUid', data.garageUid)
        if (data?.vehicleUid) form.append('vehicleUid', data.vehicleUid)
        if (data?.vehicleServiceUid) form.append('vehicleServiceUid', data.vehicleServiceUid)
        if (data?.vehicleTransferUid) form.append('vehicleTransferUid', data.vehicleTransferUid)

        const response = await fetch(`${API_URL}/api/files`, {
            method: 'POST',
            headers: { Authorization: `Bearer ${token}` },
            body: form,
        })

        if (!response.ok) {
            const err = await response.json().catch(() => ({}))
            throw new Error(err.error ?? 'Failed to upload file')
        }

        const { path, url, size, mimeType } = await response.json()

        // 2. Uložení záznamu do Firestore (url = permanentní download URL)
        const meta: Omit<IFile, 'createdAt'> = {
            createdBy: auth.currentUser!.uid,
            name: data?.name || file.name,
            path,
            url,
            size,
            mimeType,
            // garageUid/dealerId jsou vždy zapsané explicitně (i jako null) — díky tomu
            // je lze spolehlivě dotazovat (`where('dealerId', '==', null)` v Firestore
            // najde jen dokumenty, kde pole opravdu existuje s hodnotou null, ne tam,
            // kde pole úplně chybí).
            garageUid: data?.garageUid ?? null,
            dealerId: data?.dealerId ?? null,
            ...(data?.vehicleUid && { vehicleUid: data.vehicleUid }),
            ...(data?.vehicleServiceUid && { vehicleServiceUid: data.vehicleServiceUid }),
            ...(data?.vehicleTransferUid && { vehicleTransferUid: data.vehicleTransferUid }),
        }

        const docRef = await addDoc(collection(firestore, COLLECTIONS.FILE), {
            ...meta,
            createdAt: serverTimestamp(),
        })

        return { id: docRef.id, ...meta, createdAt: new Date() }
    }

    /**
     * Načte seznam souborů z Firestore s volitelnými filtry.
     * Práva jsou vynucena Firestore rules.
     * Výsledky jsou řazeny sestupně dle data vytvoření.
     * @param filters - Volitelné filtry: garageUid, vehicleUid, vehicleServiceUid
     */
    const list = async (filters?: FileFilters): Promise<FileDoc[]> => {
        const constraints = []

        if (filters?.garageUid) {
            constraints.push(where('garageUid', '==', filters.garageUid))
        }
        if (filters?.dealerId) {
            constraints.push(where('dealerId', '==', filters.dealerId))
        }
        if (filters?.vehicleServiceUid) {
            constraints.push(where('vehicleServiceUid', '==', filters.vehicleServiceUid))
        } else if (filters?.vehicleTransferUid) {
            constraints.push(where('vehicleTransferUid', '==', filters.vehicleTransferUid))
        } else if (filters?.vehicleUid) {
            constraints.push(where('vehicleUid', '==', filters.vehicleUid))
        }

        constraints.push(orderBy('createdAt', 'desc'))

        const q = query(collection(firestore, COLLECTIONS.FILE), ...constraints)
        const snapshot = await getDocs(q)
        return snapshot.docs.map(d => convertDoc(d.id, d.data()))
    }

    /**
     * Smaže soubor z Cloud Storage (přes API) a odstraní Firestore záznam.
     */
    const remove = async (file: FileDoc): Promise<void> => {
        const token = await getAuthToken()
        const res = await fetch(`${API_URL}/api/files?path=${encodeURIComponent(file.path)}`, {
            method: 'DELETE',
            headers: { Authorization: `Bearer ${token}` },
        })
        if (!res.ok) {
            const err = await res.json().catch(() => ({}))
            throw new Error(err.error ?? 'Failed to delete file')
        }
        await deleteDoc(doc(firestore, COLLECTIONS.FILE, file.id))
    }

    return { create, list, remove }
}

export default useFile
